// Inline "describe an agent, ship an agent" surface.
//
// Lifecycle inside one assistant turn:
//   loading  -> calling the chat model to draft a spec
//   review   -> editable spec card; user can tweak name/goal/tools
//   saving   -> persisting to the agents table
//   ready    -> spec saved; Run / Download / Share buttons exposed
//   error    -> spec generation or save failed
import { useEffect, useRef, useState } from "react";
import { Bot, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import type { AgentDraft } from "@/hooks/useAgents";
import { useCreateAgent } from "@/hooks/useAgents";
import { AGENT_TOOLS, type AgentToolId } from "@/lib/agentTools";
import { promptToFilename, saveJSON } from "@/lib/downloads";
import { cn } from "@/lib/utils";
import { generateAgentSpec } from "./agentSpec";
import ResultActions from "./ResultActions";

type Status = "loading" | "review" | "saving" | "ready" | "error";

export type AgentResultState = {
  prompt: string;
  status: Status;
  draft: AgentDraft | null;
  savedAgentId: string | null;
  error?: string | null;
};

type Props = {
  state: AgentResultState;
  onPatch: (patch: Partial<AgentResultState>) => void;
  onRegenerate: () => void;
};

export default function AgentResult({ state, onPatch, onRegenerate }: Props) {
  const fired = useRef(false);
  const createAgent = useCreateAgent();
  const navigate = useNavigate();

  useEffect(() => {
    if (fired.current || state.status !== "loading" || state.draft) return;
    fired.current = true;
    (async () => {
      try {
        const draft = await generateAgentSpec(state.prompt);
        onPatch({ draft, status: "review" });
      } catch (e: any) {
        const msg = e?.message ?? "Could not draft an agent";
        onPatch({ status: "error", error: msg });
        toast.error(msg);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDraft = (patch: Partial<AgentDraft>) => {
    if (!state.draft) return;
    onPatch({ draft: { ...state.draft, ...patch } });
  };

  const toggleTool = (id: AgentToolId) => {
    if (!state.draft) return;
    const has = state.draft.tools.includes(id);
    updateDraft({
      tools: has ? state.draft.tools.filter((t) => t !== id) : [...state.draft.tools, id],
    });
  };

  const save = async () => {
    if (!state.draft) return;
    try {
      onPatch({ status: "saving" });
      const agent = await createAgent.mutateAsync(state.draft);
      onPatch({ status: "ready", savedAgentId: agent.id });
      toast.success("Agent saved");
    } catch (e: any) {
      const msg = e?.message ?? "Could not save agent";
      onPatch({ status: "review", error: msg });
      toast.error(msg);
    }
  };

  if (state.status === "loading") {
    return (
      <div className="rounded-2xl border border-border bg-card/60 p-4 flex items-center gap-3">
        <Loader2 className="w-5 h-5 text-primary animate-spin" />
        <div>
          <div className="text-sm font-mono">Designing your agent…</div>
          <div className="text-[11px] text-muted-foreground">Choosing tools, writing instructions.</div>
        </div>
      </div>
    );
  }

  if (state.status === "error" || !state.draft) {
    return (
      <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4 space-y-2">
        <div className="text-sm font-mono text-destructive">{state.error ?? "Something went wrong"}</div>
        <button
          type="button"
          onClick={onRegenerate}
          className="h-7 px-2 rounded-md text-[11px] font-mono bg-secondary hover:bg-secondary/80"
        >
          Try again
        </button>
      </div>
    );
  }

  const d = state.draft;
  const isSaved = state.status === "ready" && !!state.savedAgentId;

  return (
    <div className="rounded-2xl border border-border bg-card/60 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 grid place-items-center rounded-xl bg-primary/15 text-primary shrink-0">
          <Bot className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <input
            value={d.name}
            onChange={(e) => updateDraft({ name: e.target.value })}
            disabled={isSaved}
            className="w-full bg-transparent text-sm font-mono font-semibold outline-none border-b border-transparent focus:border-primary/40"
          />
          <textarea
            value={d.description ?? ""}
            onChange={(e) => updateDraft({ description: e.target.value })}
            disabled={isSaved}
            rows={2}
            className="w-full resize-none bg-transparent text-[12px] text-muted-foreground outline-none border border-border/60 rounded-md px-2 py-1 focus:border-primary/40"
          />
        </div>
      </div>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">
          Tools
        </div>
        <div className="flex flex-wrap gap-1.5">
          {AGENT_TOOLS.map((t) => {
            const on = d.tools.includes(t.id);
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggleTool(t.id)}
                disabled={isSaved}
                title={t.description}
                className={cn(
                  "inline-flex items-center gap-1 h-7 px-2 rounded-full text-[10px] font-mono border transition-colors",
                  on
                    ? "border-primary/60 bg-primary/15 text-foreground"
                    : "border-border bg-card/40 text-muted-foreground hover:text-foreground",
                  isSaved && "opacity-70",
                )}
              >
                <Icon className="w-3 h-3" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">
          First task
        </div>
        <textarea
          value={d.default_goal}
          onChange={(e) => updateDraft({ default_goal: e.target.value })}
          disabled={isSaved}
          rows={2}
          className="w-full resize-none bg-transparent text-[12px] outline-none border border-border/60 rounded-md px-2 py-1 focus:border-primary/40"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {!isSaved ? (
          <button
            type="button"
            onClick={save}
            disabled={state.status === "saving"}
            className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground text-[11px] font-mono hover:opacity-90 disabled:opacity-50"
          >
            {state.status === "saving" ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" /> Saving…
              </>
            ) : (
              <>Save agent</>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => navigate(`/agents/builder?id=${state.savedAgentId}`)}
            className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground text-[11px] font-mono hover:opacity-90"
          >
            <Play className="w-3 h-3" /> Open & run
          </button>
        )}
        <ResultActions
          copyText={`${d.name}\n\n${d.system_prompt}`}
          onDownload={() => saveJSON(d, `${promptToFilename(d.name)}-agent.json`)}
          downloadLabel="Download .json"
          onRegenerate={!isSaved ? onRegenerate : undefined}
        />
      </div>
    </div>
  );
}